import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { sendRoomState } from 'services/webSockets/roomState'
import {
    WHITEBOARD,
    QUIZ,
    SCREEN_SHARE,
    VIDEO_CALL,
    ROOM_STATE,
    CLASSROOM,
    IFRAME,
} from 'services/webSockets/roomState/constants'
import { setMaster } from 'store/actions/Room'
import { sendSocketMessage } from 'store/actions/WebSocket'
import { dispatchUpdateIsTeacherPresent } from 'store/dispatcher/Room'
import { _isStudent } from 'store/selectors/User'
import { setTeacherGender } from 'utils/gender'

const useRoomStateHandler = roomStateMsg => {
    const {
        userId,
        setQuizMode,
        setRoomModeState,
        isWhiteboardMode,
        setWhiteboardMode,
        toggleTeacherPresent,
        toggleAnyoneScreenShare,
        setArtefactMode,
    } = useVideoContext()
    const [timeOutId, setTimeOutId] = useState(null)
    const dispatch = useDispatch()

    /* clear quizTimeout on unmount */
    let quizTimeoutId = null
    useEffect(() => {
        return () => {
            if (quizTimeoutId) {
                clearTimeout(quizTimeoutId)
                quizTimeoutId = null
            }
        }
    }, []) // do not pass dependency

    const resetTimeout = () => {
        if (timeOutId) {
            clearTimeout(timeOutId)
            setTimeOutId(null)
        }
    }

    const whiteboardStateHandler = data => {
        if (!data) return
        const isTeacherPresent = data.teacher_present
        const changeWhiteBoardMode = (mode = false) => {
            setWhiteboardMode(mode)
            setQuizMode(false)
        }
        if (isWhiteboardMode && !isTeacherPresent) {
            // if whiteboard is open & teacher is not present
            // close the whiteboard after a min
            const timeOutIdentity = setTimeout(() => {
                changeWhiteBoardMode(false)
                // if teacher is not back within a min,
                // send broadcast to change the room to video call in backend
                sendRoomState(
                    val => dispatch(sendSocketMessage(val)),
                    VIDEO_CALL
                )
            }, 60000)
            setTimeOutId(timeOutIdentity)
        } else {
            // teacher is present & whiteboard is not open
            // then turn on whiteboard
            changeWhiteBoardMode(true)
            resetTimeout()
        }
    }

    const videoCallStateHandler = data => {
        if (!data) return
        setWhiteboardMode(false)
        toggleAnyoneScreenShare(false)
        // delay quiz unmount so users can see Quiz End Screen
        quizTimeoutId = setTimeout(() => setQuizMode(false), 3000)
        setRoomModeState(VIDEO_CALL)
        resetTimeout()
    }

    const quizStateHandler = data => {
        if (!data || !data.teacher_present) return
        setWhiteboardMode(false)
        setRoomModeState(QUIZ)
        setQuizMode(true)
    }

    const screenShareHandler = data => {
        if (!data?.data?.userId) return

        if (data.data.userId !== userId) {
            setQuizMode(false)
            setWhiteboardMode(false)
            toggleAnyoneScreenShare(true)
        }
    }

    const classroomProtocolHandler = data => {
        if (!data) return
    }

    const roomStateHandler = data => {
        if (!data) return
        toggleTeacherPresent(data.teacher_present)
        dispatchUpdateIsTeacherPresent(data.teacher_present)
        setTeacherGender(data.teacher_gender)
        dispatch(
            setMaster({
                master: data.master,
                isMaster: data.master === userId,
            })
        )

        switch (data.state) {
            case WHITEBOARD:
                whiteboardStateHandler(data)
                break
            case QUIZ:
                quizStateHandler(data)
                break
            case SCREEN_SHARE:
                screenShareHandler(data)
                break
            case VIDEO_CALL:
                videoCallStateHandler(data)
                break
            default:
                break
        }
        switch (data.protocol) {
            case CLASSROOM:
                classroomProtocolHandler(data)
                break
            default:
                break
        }
    }

    useEffect(() => {
        if (
            !roomStateMsg ||
            roomStateMsg.action !== ROOM_STATE ||
            (roomStateMsg.state === QUIZ && !roomStateMsg.teacher_present)
        )
            return
        roomStateHandler(roomStateMsg)
        const isArtefactMode = [
            SCREEN_SHARE,
            QUIZ,
            WHITEBOARD,
            IFRAME,
        ].includes(roomStateMsg.state)
        setArtefactMode(isArtefactMode)
    }, [roomStateMsg])
}

export default useRoomStateHandler
